import { useEffect, useState, useCallback, useRef } from "react";
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  WrenchIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/20/solid";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { toast } from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  BuildingOffice2Icon,
  CalendarDaysIcon,
  ClipboardDocumentListIcon,
  EnvelopeOpenIcon,
} from "@heroicons/react/24/outline";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import { fetchUsers } from "../../../api/users";
import { updateUrlWithException, status } from "../../../components/Utils";
import {
  format,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  addWeeks,
  subWeeks,
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  addDays,
  subDays,
  parseISO,
  isValid,
  isSameMonth,
} from "date-fns";
import { nb } from "date-fns/locale";
import axios from "../../../api/axios";
import { useUserHasAccess } from "../../../routes/roleOnly";
import { RenderDayView, formatEventLocalTimeRange } from "./utils";
import { ReportHoursTag } from "../../../components/dashboard/service/reports/report-hours-tag";
import { ItemListProductStatusTag } from "../../../components/dashboard/service/reports/item-list-product-status-tag";

// Hjelpefunksjon for å runde ned til nærmeste halvtime
function toHalfHourDecimal(hour, minute) {
  return hour + (minute < 30 ? 0 : 0.5);
}

// Hjelpefunksjon for å tildele slots til overlappende events
function assignEventSlots(events) {
  // Sorter events etter start
  const sorted = [...events].sort((a, b) => a._start - b._start);
  let slots = [];
  sorted.forEach((event) => {
    let placed = false;
    for (let i = 0; i < slots.length; i++) {
      if (slots[i][slots[i].length - 1]._end <= event._start) {
        slots[i].push(event);
        event._slot = i;
        placed = true;
        break;
      }
    }
    if (!placed) {
      slots.push([event]);
      event._slot = slots.length - 1;
    }
  });
  return { events: sorted, slotCount: slots.length };
}

function shouldMergeProjectPlanEvents(modules) {
  const m = Array.isArray(modules) ? modules : [];
  return m.length === 0 || m.includes("project_plan");
}

function calendarDateRangeForProjectEvents(controller) {
  const base = new Date(`${controller.date}T12:00:00`);
  switch (controller.view) {
    case "day":
      return {
        date_from: format(base, "yyyy-MM-dd"),
        date_to: format(base, "yyyy-MM-dd"),
      };
    case "week": {
      const ws = startOfWeek(base, { weekStartsOn: 1 });
      const we = endOfWeek(base, { weekStartsOn: 1 });
      return {
        date_from: format(ws, "yyyy-MM-dd"),
        date_to: format(we, "yyyy-MM-dd"),
      };
    }
    default: {
      const ms = startOfMonth(base);
      const me = endOfMonth(base);
      return {
        date_from: format(ms, "yyyy-MM-dd"),
        date_to: format(me, "yyyy-MM-dd"),
      };
    }
  }
}

function projectEventVisibleForCalendarUsers(pe, userIds) {
  const set = new Set((userIds || []).map(String));
  if (set.size === 0) return true;
  const res = pe.resources;
  // If a ProjectPlan event has no assigned resources, it should NOT appear in personal calendars.
  // It becomes visible once at least one resource/user is assigned.
  if (!Array.isArray(res) || res.length === 0) return false;
  return res.some((r) => {
    const rid =
      r?.user_id ??
      r?.user?.id ??
      r?.user?.value; // defensive: some endpoints may return selector-shaped objects
    return rid != null && set.has(String(rid));
  });
}

function expandProjectEventToCalendarEntries(pe, activityFallbackLabel) {
  const pa = pe.project_activity;
  const startRaw = pe.start_date;
  const endRaw = pe.end_date || pe.start_date;
  if (!startRaw) return [];
  const startStr =
    typeof startRaw === "string"
      ? startRaw.substring(0, 10)
      : format(new Date(startRaw), "yyyy-MM-dd");
  const endStr =
    typeof endRaw === "string"
      ? endRaw.substring(0, 10)
      : format(new Date(endRaw), "yyyy-MM-dd");
  const startDate = parseISO(startStr);
  const endDate = parseISO(endStr);
  if (!isValid(startDate) || !isValid(endDate)) return [];
  const days = eachDayOfInterval({ start: startDate, end: endDate });
  const activityLabel = pa?.display_title || pa?.title;
  return days.map((day) => {
    const d = format(day, "yyyy-MM-dd");
    return {
      id: `project_event_${pe.id}_${d}`,
      title: pe.title || activityLabel || activityFallbackLabel,
      description: pe.description,
      start_time: `${d}T08:00:00`,
      end_time: `${d}T17:00:00`,
      module: "project_plan",
      module_id: pe.id,
      project_id: pe.project_id,
      user: null,
      all_day: false,
      location: null,
      module_details: {
        displayTitle: pe.title || activityLabel,
        description: pe.description,
        project_activity: pa,
        project: pe.project,
      },
    };
  });
}

export const CalendarView = () => {
  const currentUser = useSelector((state) => state.session.currentUser);

  const isManager = useUserHasAccess({ role: "calendar_manage" });
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const settings = useSelector((state) => state.session.currentUser.settings);
  const currentUserSession = useSelector((state) => state.session.currentUser);

  const today = new Date();

  // Controller pattern for managing state and URL params
  const [controller, setController] = useState({
    date: searchParams.get("date") || today.toISOString().substring(0, 10),
    view: searchParams.get("view") || "month",
    query: searchParams.get("query") || "",
    statuses: searchParams.get("statuses")
      ? searchParams.get("statuses").split(",")
      : [],
    projects: searchParams.get("projects")
      ? searchParams.get("projects").split(",")
      : [],
    modules: searchParams.get("modules")
      ? searchParams.get("modules").split(",")
      : [],
    users: searchParams.get("users")
      ? searchParams.get("users").split(",")
      : [currentUser.id.toString()],
    showWeekNumbers: searchParams.get("showWeekNumbers") === "true",
  });

  const [userOptions, setUserOptions] = useState({
    isLoading: true,
    error: false,
    errors: [],
    data: [],
  });

  const [projectOptions, setProjectOptions] = useState({
    isLoading: true,
    error: false,
    errors: [],
    data: [],
  });

  const [events, setEvents] = useState({
    isLoading: true,
    error: false,
    data: [],
  });

  // Use ref to track previous controller
  const prevControllerRef = useRef();

  // Module options for filtering
  const moduleOptions = [
    { value: "report", label: "Service Reports" },
    { value: "absence", label: "Absences" },
    { value: "case", label: "Cases" },
    { value: "order", label: "Orders" },
    { value: "project", label: "Projects" },
    { value: "work_order", label: t("work_orders.calendar_filter") },
    { value: "project_plan", label: t("project_plan") },
  ];

  // Fetch users for filtering
  async function fetchAndMapUsers() {
    setUserOptions((prevState) => ({
      ...prevState,
      isLoading: true,
    }));

    const controller = {
      filters: [],
      page: 1,
      query: "",
      sort: "desc",
      sortBy: "id",
      view: "all",
    };

    try {
      const res = await fetchUsers(controller);
      if (res.data) {
        const array = res.data.map((row, i) => ({
          value: row.id,
          label: row.name,
          order: i,
        }));
        setUserOptions({
          isLoading: false,
          data: isManager
            ? array
            : array.filter((user) => user.value === currentUser.id),
          error: false,
          errors: [],
        });
      }
    } catch (err) {
      setUserOptions((prevState) => ({
        ...prevState,
        isLoading: false,
        error: true,
        errors: err,
      }));
    }
  }

  // Fetch projects for filtering
  async function fetchAndMapProjects() {
    setProjectOptions((prevState) => ({
      ...prevState,
      isLoading: true,
    }));

    try {
      // This would need to be implemented based on your project API
      const res = await fetch("/api/projects", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      const data = await res.json();

      if (data) {
        const array = data.map((row, i) => ({
          value: row.id,
          label: row.name,
          order: i,
        }));
        setProjectOptions({
          isLoading: false,
          data: array,
          error: false,
          errors: [],
        });
      }
    } catch (err) {
      setProjectOptions((prevState) => ({
        ...prevState,
        isLoading: false,
        error: true,
        errors: err,
      }));
    }
  }

  // Fetch events based on controller
  const fetchEvents = async () => {
    setEvents((prevState) => ({
      ...prevState,
      isLoading: true,
    }));

    try {
      const params = new URLSearchParams({
        date: controller.date,
        view: controller.view,
        query: controller.query,
        statuses: Array.isArray(controller.statuses)
          ? controller.statuses.join(",")
          : "",
        projects: Array.isArray(controller.projects)
          ? controller.projects.join(",")
          : "",
        modules: Array.isArray(controller.modules)
          ? controller.modules.join(",")
          : "",
        users: Array.isArray(controller.users)
          ? controller.users.join(",")
          : "",
      });

      const res = await axios.get(`/events?${params}`);

      let data = Array.isArray(res.data) ? res.data : [];

      if (shouldMergeProjectPlanEvents(controller.modules)) {
        const { date_from, date_to } =
          calendarDateRangeForProjectEvents(controller);
        const peParams = new URLSearchParams({
          date_from,
          date_to,
          per_page: "200",
          fields: "*",
        });
        if (controller.projects?.length === 1) {
          peParams.set("project_id", controller.projects[0]);
        }
        try {
          const peRes = await axios.get(`/project_events?${peParams}`);
          const raw = peRes.data?.data;
          const list = Array.isArray(raw) ? raw : [];
          const planLabel = t("project_plan");
          const projectFilter =
            controller.projects?.length > 1
              ? new Set(controller.projects.map(String))
              : null;
          for (const pe of list) {
            if (!projectEventVisibleForCalendarUsers(pe, controller.users)) {
              continue;
            }
            if (projectFilter && !projectFilter.has(String(pe.project_id))) {
              continue;
            }
            data = data.concat(
              expandProjectEventToCalendarEntries(pe, planLabel)
            );
          }
        } catch (peErr) {
          console.error("Error fetching project plan events for calendar:", peErr);
        }
      }

      data.sort((a, b) => {
        const ta = new Date(a.start_time).getTime();
        const tb = new Date(b.start_time).getTime();
        return ta - tb;
      });

      setEvents({
        isLoading: false,
        data,
        error: false,
      });
    } catch (err) {
      console.error("Error fetching events:", err);
      setEvents({
        isLoading: false,
        data: [],
        error: true,
      });
      toast.error("Failed to load events");
    }
  };

  // Update controller and URL
  const updateController = useCallback((newController) => {
    setController(newController);

    // Update URL params
    updateUrlWithException(
      {
        date: newController.date,
        view: newController.view,
        query: newController.query,
        statuses: Array.isArray(newController.statuses)
          ? newController.statuses.join(",")
          : "",
        projects: Array.isArray(newController.projects)
          ? newController.projects.join(",")
          : "",
        modules: Array.isArray(newController.modules)
          ? newController.modules.join(",")
          : "",
        users: Array.isArray(newController.users)
          ? newController.users.join(",")
          : "",
        showWeekNumbers: newController.showWeekNumbers.toString(),
      },
      "filters"
    );
  }, []);

  // Navigation functions
  const navigateDate = (direction) => {
    const currentDate = new Date(controller.date);
    let newDate;

    switch (controller.view) {
      case "day":
        newDate =
          direction === "next"
            ? addDays(currentDate, 1)
            : subDays(currentDate, 1);
        break;
      case "week":
        newDate =
          direction === "next"
            ? addWeeks(currentDate, 1)
            : subWeeks(currentDate, 1);
        break;
      case "month":
      default:
        newDate =
          direction === "next"
            ? addMonths(currentDate, 1)
            : subMonths(currentDate, 1);
        break;
    }

    updateController({
      ...controller,
      date: newDate.toISOString().substring(0, 10),
    });
  };

  const setViewMode = (view) => {
    updateController({
      ...controller,
      view,
    });
  };

  const setSelectedDate = (date) => {
    updateController({
      ...controller,
      date,
    });
  };

  const handleFiltersChange = useCallback(
    (newFilters) => {
      updateController({
        ...controller,
        query: newFilters.query || "",
        statuses: Array.isArray(newFilters.statuses) ? newFilters.statuses : [],
        projects: Array.isArray(newFilters.projects) ? newFilters.projects : [],
        modules: Array.isArray(newFilters.modules) ? newFilters.modules : [],
        users: Array.isArray(newFilters.users)
          ? newFilters.users
          : [currentUser.id.toString()],
      });
    },
    [controller, updateController]
  );

  useEffect(() => {
    fetchAndMapUsers();
    fetchAndMapProjects();
  }, []);

  // Check if controller has changed and only fetch if it has
  useEffect(() => {
    const prevController = prevControllerRef.current;

    // Skip on first render
    if (prevController === undefined) {
      prevControllerRef.current = controller;
      fetchEvents();
      return;
    }

    // Check if any controller property has changed
    const hasChanged =
      JSON.stringify(controller) !== JSON.stringify(prevController);

    if (hasChanged) {
      prevControllerRef.current = controller;
      fetchEvents();
    }
  }, [controller]);

  const navigate = useNavigate();

  const navigateToEvent = (event) => {
    // Use original_id if this is a working day event, otherwise use the event id
    const eventId = event.original_id || event.id;

    switch (event.module) {
      case "report":
        navigate(`/dashboard/service/reports/${event.module_id}`);
        break;
      case "absence":
        navigate(`/dashboard/absences/${event.module_id}`);
        break;
      case "case":
        navigate(`/dashboard/cases/${event.module_id}`);
        break;
      case "order":
        navigate(`/dashboard/orders/${eventId}`);
        break;
      case "project":
        navigate(`/dashboard/projects/${eventId}`);
        break;
      case "work_order":
        navigate(`/dashboard/work_orders/${event.module_id}`);
        break;
      default:
        break;
    }
  };

  const getEventIcon = (module) => {
    switch (module) {
      case "report":
        return <WrenchIcon className="h-5 w-5" />;
      case "absence":
        return <ExclamationTriangleIcon className="h-5 w-5" />;
      case "case":
        return <EnvelopeOpenIcon className="h-5 w-5" />;
      case "order":
        return <ClipboardDocumentListIcon className="h-5 w-5" />;
      case "project":
        return <BuildingOffice2Icon className="h-5 w-5" />;
      case "work_order":
        return <ClipboardDocumentListIcon className="h-5 w-5" />;
      case "project_plan":
        return <CalendarDaysIcon className="h-5 w-5" />;
      default:
        return <CalendarIcon className="h-5 w-5" />;
    }
  };

  const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

  // Process events based on working hours to show them only on working days
  const processEventsForWorkingHours = (events) => {
    if (!events || !Array.isArray(events)) return [];

    return events.flatMap((event) => {
      // Only expand true all-day events; timed events may still carry stale type "all_day" + bad schedule (drops from calendar otherwise).
      const wh = event.working_hours;
      if (
        !wh ||
        wh.type !== "all_day" ||
        !Array.isArray(wh.schedule) ||
        !event.all_day
      ) {
        return [event];
      }

      const processedEvents = [];
      const schedule = wh.schedule;

      schedule.forEach((daySchedule) => {
        if (daySchedule.day_off) {
          return;
        }

        const dateStr = daySchedule.date;
        if (!dateStr || !ISO_DATE_RE.test(String(dateStr))) {
          return;
        }

        const startTime = daySchedule.start_time;
        const endTime = daySchedule.end_time;

        if (startTime && endTime) {
          const startTimeString = `${dateStr}T${startTime}:00`;
          const endTimeString = `${dateStr}T${endTime}:00`;
          const startProbe = new Date(startTimeString);
          if (Number.isNaN(startProbe.getTime())) {
            return;
          }

          processedEvents.push({
            ...event,
            id: `${event.id}_${dateStr}`,
            original_id: event.id,
            start_time: startTimeString,
            end_time: endTimeString,
            working_day: dateStr,
            working_hours: wh,
            is_working_day_event: true,
          });
        }
      });

      return processedEvents.length > 0 ? processedEvents : [event];
    });
  };

  // Get events for a specific day, considering working hours
  const getEventsForDay = (day, allEvents) => {
    const processedEvents = processEventsForWorkingHours(allEvents);

    const dayEvents = processedEvents.filter((event) => {
      const eventDate = new Date(event.start_time);
      const isSame = isSameDay(eventDate, day);

      return isSame;
    });

    return dayEvents;
  };

  const getEventColor = (module) => {
    switch (module) {
      case "report":
        return "bg-blue-100 text-blue-800";
      case "absence":
        return "bg-yellow-100 text-yellow-800";
      case "case":
        return "bg-green-100 text-green-800";
      case "order":
        return "bg-purple-100 text-purple-800";
      case "project":
        return "bg-indigo-100 text-indigo-800";
      case "work_order":
        return "bg-amber-100 text-amber-900";
      case "project_plan":
        return "bg-teal-100 text-teal-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getCurrentPeriodText = () => {
    const currentDate = new Date(controller.date);

    switch (controller.view) {
      case "day": {
        return format(currentDate, "EEEE, d. MMMM yyyy", { locale: nb });
      }
      case "week": {
        const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
        return `${format(weekStart, "d. MMM", { locale: nb })} - ${format(
          weekEnd,
          "d. MMM yyyy",
          { locale: nb }
        )}`;
      }
      case "month":
      default: {
        return format(currentDate, "MMMM yyyy", { locale: nb });
      }
    }
  };

  const renderMonthView = () => {
    const monthStart = startOfMonth(new Date(controller.date));
    const monthEnd = endOfMonth(new Date(controller.date));
    // Align grid with Mon–Sun: include leading/trailing days from adjacent months
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

    return (
      <div className="grid grid-cols-7 gap-1">
        {["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"].map((day) => (
          <div
            key={day}
            className="p-2 text-center text-sm font-medium text-gray-500"
          >
            {day}
          </div>
        ))}
        {days.map((day) => {
          const inCurrentMonth = isSameMonth(day, monthStart);
          const dayEvents = getEventsForDay(day, events.data);

          return (
            <div
              key={format(day, "yyyy-MM-dd")}
              className={`min-h-[100px] p-2 border border-gray-200 ${
                isSameDay(day, today) ? "bg-blue-50" : ""
              } ${!inCurrentMonth ? "bg-gray-50 text-gray-400" : ""}`}
            >
              <div
                className={`text-sm font-medium ${
                  inCurrentMonth ? "text-gray-900" : "text-gray-400"
                }`}
              >
                {format(day, "d")}
              </div>
              <div className="mt-1 space-y-1">
                {dayEvents.slice(0, 3).map((event) => (
                  <div
                    key={event.id}
                    onClick={() => navigateToEvent(event)}
                    className={`text-xs p-1 rounded cursor-pointer ${getEventColor(
                      event.module
                    )}`}
                  >
                    <div className="font-medium">{event.title}</div>
                    <div className="flex items-center justify-between gap-1">
                      <div className="text-xs opacity-75">
                        {event.is_working_day_event &&
                          typeof event.working_hours === "number" && (
                            <span>{event.working_hours}h</span>
                          )}
                        {event.is_working_day_event &&
                          typeof event.working_hours === "object" &&
                          event.working_day && (
                            <span>
                              {(() => {
                                const info = event.working_hours.schedule?.find(
                                  (s) => s.date === event.working_day
                                );
                                return info ? `${info.hours}h` : "";
                              })()}
                            </span>
                          )}
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-0.5">
                        <ReportHoursTag event={event} />
                        <ItemListProductStatusTag event={event} />
                      </div>
                    </div>
                  </div>
                ))}
                {dayEvents.length > 3 && (
                  <div className="text-xs text-gray-500">
                    +{dayEvents.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderWeekView = () => {
    const start = startOfWeek(new Date(controller.date), { weekStartsOn: 1 });
    const end = endOfWeek(new Date(controller.date), { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start, end });

    return (
      <div className="grid grid-cols-8 gap-1">
        <div className="p-2 text-center text-sm font-medium text-gray-500">
          {controller.showWeekNumbers ? `Week ${format(start, "w")}` : "Time"}
        </div>
        {["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"].map((day) => (
          <div
            key={day}
            className="p-2 text-center text-sm font-medium text-gray-500"
          >
            {day}
          </div>
        ))}
        <div className="p-2 text-center text-sm font-medium text-gray-500">
          {controller.showWeekNumbers ? `Week ${format(start, "w")}` : "Time"}
        </div>
        {days.map((day) => {
          const dayEvents = getEventsForDay(day, events.data);

          return (
            <div
              key={day.toISOString()}
              className={`min-h-[120px] p-2 border border-gray-200 ${
                isSameDay(day, today) ? "bg-blue-50" : ""
              }`}
            >
              <div className="text-sm font-medium text-gray-900">
                {format(day, "d")}
              </div>
              <div className="mt-1 space-y-1">
                {dayEvents.map((event) => (
                  <div
                    key={event.id}
                    onClick={() => navigateToEvent(event)}
                    className={`text-xs p-1 rounded cursor-pointer ${getEventColor(
                      event.module
                    )}`}
                  >
                    <div className="font-medium">{event.title}</div>
                    <div className="flex items-center justify-between gap-1">
                      <div className="text-xs opacity-75">
                        {event.is_working_day_event && event.working_hours
                          ? `${event.working_hours}h`
                          : event.time || "All day"}
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-0.5">
                        <ReportHoursTag event={event} />
                        <ItemListProductStatusTag event={event} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Legg til state for dagvisningstype
  const [dayViewType, setDayViewType] = useState("standard");

  // Toggle for dagvisningstype
  const DayViewToggle = () => (
    <div className="mb-4 flex gap-2">
      <button
        className={`px-3 py-1 rounded ${
          dayViewType === "standard" ? "bg-blue-600 text-white" : "bg-gray-200"
        }`}
        onClick={() => setDayViewType("standard")}
      >
        Standard
      </button>
      <button
        className={`px-3 py-1 rounded ${
          dayViewType === "outlook" ? "bg-blue-600 text-white" : "bg-gray-200"
        }`}
        onClick={() => setDayViewType("outlook")}
      >
        Outlook
      </button>
    </div>
  );

  // Outlook-lignende dagvisning
  const renderOutlookDayView = () => {
    const GRID_ROW_HEIGHT = 54; // px per halvtime
    const hours = Array.from({ length: 24 * 2 }, (_, i) => i / 2); // 0, 0.5, 1, 1.5, ...
    const dayEvents = getEventsForDay(new Date(controller.date), events.data);

    // Beregn _start og _end for alle events
    const eventsWithTimes = dayEvents.map((event) => {
      let workingDayInfo = null;
      if (
        event.working_hours &&
        typeof event.working_hours === "object" &&
        event.working_hours.schedule &&
        Array.isArray(event.working_hours.schedule) &&
        event.working_day
      ) {
        workingDayInfo = event.working_hours.schedule.find(
          (s) => s.date === event.working_day
        );
      }
      let start = 0,
        end = 1;
      if (workingDayInfo && !workingDayInfo.day_off) {
        const [workingStartHour, workingStartMinute] = workingDayInfo.start_time
          .split(":")
          .map(Number);
        const [workingEndHour, workingEndMinute] = workingDayInfo.end_time
          .split(":")
          .map(Number);
        start = toHalfHourDecimal(workingStartHour, workingStartMinute);
        end = toHalfHourDecimal(workingEndHour, workingEndMinute);
      } else if (event.start_time && event.end_time) {
        // Parse the time from the ISO string and convert to local time
        const startDate = new Date(event.start_time);
        const endDate = new Date(event.end_time);

        // Use local time (getHours() instead of getUTCHours())
        const startHours = startDate.getHours();
        const startMinutes = startDate.getMinutes();
        const endHours = endDate.getHours();
        const endMinutes = endDate.getMinutes();

        start = toHalfHourDecimal(startHours, startMinutes);
        end = toHalfHourDecimal(endHours, endMinutes);
      }
      return { ...event, _start: start, _end: end };
    });

    // Tildel slots til overlappende events
    const { events: slottedEvents, slotCount } =
      assignEventSlots(eventsWithTimes);

    return (
      <div
        className="relative border rounded-lg overflow-hidden"
        style={{ minHeight: 24 * 2 * GRID_ROW_HEIGHT }}
      >
        {/* Debug-linje for 08:00 */}
        <div
          style={{
            position: "absolute",
            top: 8 * 2 * GRID_ROW_HEIGHT, // 08:00 (16 halvtime-rader)
            left: 0,
            right: 0,
            height: 2,
            background: "red",
            zIndex: 100,
          }}
        />
        {/* Debug-linje for 18:00 */}
        <div
          style={{
            position: "absolute",
            top: 18 * 2 * GRID_ROW_HEIGHT, // 18:00 (36 halvtime-rader)
            left: 0,
            right: 0,
            height: 2,
            background: "green",
            zIndex: 100,
          }}
        />
        {hours.map((hour) => (
          <div
            key={hour}
            className="border-b"
            style={{
              height: GRID_ROW_HEIGHT,
              display: "flex",
              alignItems: "center",
              paddingLeft: 8,
              fontSize: 12,
              color: "#64748b",
            }}
          >
            {Number.isInteger(hour)
              ? `${hour.toString().padStart(2, "0")}:00`
              : `${Math.floor(hour).toString().padStart(2, "0")}:30`}
          </div>
        ))}
        {slottedEvents.map((event) => {
          let workingDayInfo = null;
          if (
            event.working_hours &&
            typeof event.working_hours === "object" &&
            event.working_hours.schedule &&
            Array.isArray(event.working_hours.schedule) &&
            event.working_day
          ) {
            workingDayInfo = event.working_hours.schedule.find(
              (s) => s.date === event.working_day
            );
          }
          return (
            <div
              key={event.id}
              className="absolute bg-blue-200 rounded px-2 py-1 border border-blue-400 shadow"
              style={{
                top: event._start * GRID_ROW_HEIGHT,
                height: Math.max(
                  (event._end - event._start) * GRID_ROW_HEIGHT,
                  18
                ),
                left: `${(event._slot * 100) / slotCount}%`,
                width: `${100 / slotCount}%`,
                zIndex: 10,
              }}
            >
              <div className="font-semibold">{event.title}</div>
              <div className="text-xs">
                {workingDayInfo && !workingDayInfo.day_off
                  ? `${workingDayInfo.start_time} - ${workingDayInfo.end_time}`
                  : event.start_time && event.end_time
                  ? formatEventLocalTimeRange(event.start_time, event.end_time)
                  : ""}
              </div>
              {workingDayInfo && !workingDayInfo.day_off && (
                <div className="text-xs text-blue-900 font-semibold">
                  {workingDayInfo.start_time} - {workingDayInfo.end_time} (
                  {workingDayInfo.hours} t)
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const dayEvents = getEventsForDay(new Date(controller.date), events.data);

  if (events.isLoading) return <ResourceLoading />;

  return (
    <div className="bg-white px-5 py-5 rounded-2xl shadow">
      {/* Header with Navigation */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-base font-semibold leading-6 text-gray-900">
          {t("Kalender")}
        </h2>

        {/* View Mode Toggle */}
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setViewMode("month")}
            className={`px-3 py-1 text-sm rounded-md ${
              controller.view === "month"
                ? "bg-blue-600 text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            {t("Month")}
          </button>
          <button
            onClick={() => setViewMode("week")}
            className={`px-3 py-1 text-sm rounded-md ${
              controller.view === "week"
                ? "bg-blue-600 text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            {t("Week")}
          </button>
          <button
            onClick={() => setViewMode("day")}
            className={`px-3 py-1 text-sm rounded-md ${
              controller.view === "day"
                ? "bg-blue-600 text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            {t("Day")}
          </button>
        </div>
      </div>

      {/* Navigation and Date Picker */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigateDate("prev")}
            className="p-2 text-gray-400 hover:text-gray-500"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>

          <div className="text-lg font-medium text-gray-900">
            {getCurrentPeriodText()}
          </div>

          <button
            onClick={() => navigateDate("next")}
            className="p-2 text-gray-400 hover:text-gray-500"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Date Picker */}
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-700">
            {t("Date")}:
          </label>
          <input
            type="date"
            value={controller.date}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6">
        <ModuleFilter
          onFiltersChange={handleFiltersChange}
          initialFilters={{
            query: controller.query,
            statuses: controller.statuses,
            projects: controller.projects,
            modules: controller.modules,
            users: controller.users,
          }}
          acceptedStatuses={Object.keys(status)}
          searchPlaceholder="Search events..."
          filterTitle="Filter Events"
          moduleOptions={moduleOptions}
          userOptions={userOptions.data}
          projectOptions={projectOptions.data}
        />

        {/* Week Numbers Toggle */}
        <div className="mt-4 flex items-center">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={controller.showWeekNumbers}
              onChange={(e) =>
                updateController({
                  ...controller,
                  showWeekNumbers: e.target.checked,
                })
              }
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              {t("Show Week Numbers")}
            </span>
          </label>
        </div>
      </div>

      {/* Calendar Content */}
      <div className="w-full">
        {controller.view === "month" && renderMonthView()}
        {controller.view === "week" && renderWeekView()}
        {controller.view === "day" && (
          <>
            <DayViewToggle />
            {dayViewType === "standard" ? (
              <RenderDayView dayEvents={dayEvents} date={controller.date} />
            ) : (
              renderOutlookDayView()
            )}
          </>
        )}
      </div>
    </div>
  );
};
